import { prefersReducedMotion } from "@norkron/web-motion";

/**
 * `scrollMotion` — elaborate's scroll-INTENT policy. One decision, in one place.
 *
 * WHAT THIS IS NOT. It is not an animation. It contains no tween, no rAF loop,
 * no easing curve and no timer, and `scrollMotion.test.ts` asserts exactly that
 * by counting the rAFs and timers a reveal schedules (zero). RULE ZERO: the
 * motion is the PLATFORM's (`Element.scrollIntoView({behavior})` /
 * `@norkron/web-page-layout`'s surface `scrollTo`), and the
 * `prefers-reduced-motion` query is `@norkron/web-motion`'s — the ecosystem's
 * canonical owner of animation primitives, whose own conformance codebook
 * defines `MT_REDUCED_MOTION_NOT_RESPECTED` for precisely this failure. The only
 * thing that lives here is the product judgement the libs cannot make for us:
 * WHICH of elaborate's jumps is a navigation the reviewer is watching.
 *
 * WHY THE PLATFORM AND NOT A TWEEN. A native smooth scroll runs off the main
 * thread, is cancelled by the browser the instant the user wheels/touches/keys
 * (so it can never fight them), and a second `scrollIntoView` RETARGETS the
 * in-flight animation rather than stacking a second one. A hand-rolled rAF tween
 * would have to re-earn all three, and would get them wrong.
 *
 * WHY REDUCED MOTION STILL NEEDS AN EXPLICIT GATE. `scroll-behavior: smooth` in
 * CSS is UA-gated by the preference; a SCRIPTED `behavior: "smooth"` is not —
 * the script asked for it, so the engine obeys. Every animated jump in this app
 * therefore resolves its behavior through {@link scrollBehaviorFor}, never by
 * hard-coding `"smooth"`.
 *
 * THE TWO INTENTS (design call, stated so a future call site can classify itself):
 *
 *  · `navigate` — the reviewer acted and is watching the result: clicking a
 *    comment card's "jump to the anchored text", a counterpart chip, a timeline
 *    tick, a section link. The motion carries the spatial relationship between
 *    where they were and where they landed, and on the tiled canvas it is also
 *    what lets tiles paint as they are revealed instead of in one blank lurch.
 *
 *  · `restore` — the app moved the viewport for its own reasons and nobody is
 *    watching it happen: landing on a `?comment=`/`?c=` deep link at page load,
 *    re-seating the view after a re-render. Animating those is noise, it races
 *    the browser's own scroll restoration, and it delays the content the user
 *    opened the link for. These JUMP.
 */

/** Whether a scroll is a navigation the user is watching, or a restore. */
export type ScrollIntent = "navigate" | "restore";

export interface RevealOptions {
  /** Block-axis alignment; defaults to `"center"`. */
  readonly block?: ScrollLogicalPosition;
  /** Inline-axis alignment. Omitted entirely when not given. */
  readonly inline?: ScrollLogicalPosition;
}

/**
 * The resolved `ScrollBehavior` for `intent`, read from the live OS preference
 * on EVERY call (a mid-session "Reduce motion" toggle takes effect immediately —
 * no reload, no cached module-scope boolean).
 */
export function scrollBehaviorFor(intent: ScrollIntent): ScrollBehavior {
  if (intent === "restore") return "auto";
  return prefersReducedMotion() ? "auto" : "smooth";
}

/**
 * Bring a DOM element into view with the behavior `intent` resolves to, and
 * return that behavior so a caller (or a test) can assert what actually reached
 * the platform rather than trusting a comment.
 */
export function revealElement(
  el: Element,
  intent: ScrollIntent,
  opts?: RevealOptions,
): ScrollBehavior {
  const behavior = scrollBehaviorFor(intent);
  // Built key-by-key: an explicit `inline: undefined` is not the same request
  // as an absent `inline` in every engine.
  const options: ScrollIntoViewOptions = {
    behavior,
    block: opts?.block ?? "center",
  };
  if (opts?.inline !== undefined) options.inline = opts.inline;
  el.scrollIntoView(options);
  return behavior;
}

/**
 * The canvas half of the same decision.
 *
 * `@norkron/web-page-layout`'s surface owns the document's scroll geometry AND
 * its motion: `scrollTo` eases the NATIVE scroller's `scrollTop` with a rAF
 * tween, withholds the tween under `prefers-reduced-motion`, cancels on user
 * input, retargets rather than stacking, clamps at the document end, and moves
 * NOTHING when the target is already in view (`ifNeeded`, the no-jerk rule).
 *
 * So this passes ONE bit — is this a navigation or a restore — and re-decides
 * nothing (RULE ZERO). In particular the reduced-motion check is NOT repeated
 * here for the canvas: the engine that runs the tween is the one that must
 * refuse to run it, and duplicating the query product-side would be a second
 * opinion that can drift from the first. The DOM path above is different and
 * DOES need its own gate, because there the animator is the browser and a
 * SCRIPTED `behavior:"smooth"` is not UA-gated by the preference.
 *
 * Returns the surface's own verdict unchanged — a rect it could not place is
 * still a miss, and the caller still has to say so out loud.
 *
 * THAT VERDICT ONLY BECAME TRUSTWORTHY IN `web-page-layout` d8c97a2, and the
 * history is worth keeping because it is the failure mode this whole seam is
 * shaped against. Until then the engine picked its scroller with
 * `scrollHeight > clientHeight`, and a canvas page has a FRACTIONAL CSS height
 * (860.390625 measured live), so a content-driven `overflow: auto` box reports
 * 861 against 860 — one pixel of travel that does not exist. The engine bound to
 * THAT box, clamped every destination in the document to 1px, and returned
 * `true`. So `focusAnchorOnSurface` reported success, `focusComment` set
 * non-null state, no toast fired, and a real narrate drive of a comment-anchor
 * click moved nothing across 528 sampled rAF frames. On all three canvas mounts
 * the box that really scrolls is a GRANDPARENT of the mount
 * (`.nk-review-workbench__column` 139/465, `.shell__main` 772).
 *
 * The engine now resolves the scroller by REACHABILITY, per navigation, and
 * returns `false` when nothing in the chain can reach the band. Nothing changes
 * on this side — passing the verdict through unedited is what let the fix land
 * upstream and reach every call site at once.
 */
export function revealSurfaceRects<Rect>(
  surface: {
    scrollTo(
      rects: readonly Rect[],
      opts?: { readonly animate?: boolean },
    ): boolean;
  },
  rects: readonly Rect[],
  intent: ScrollIntent,
): boolean {
  if (rects.length === 0) return false;
  return surface.scrollTo(rects, { animate: intent === "navigate" });
}
